// Note playback for the score (§13.4): sampled instrument notes repitched from the nearest
// rendered reference (≤ ±6 semitones), drum one-shots, and live detuned-saw pads (max 6 voices).
import { bufferGain, type SoundStore } from '../prerender';
import { dbToGain } from '../util';
import { DRUMS, INSTRUMENTS, nearestReference, referenceNotes } from './instruments';
import type { DrumId, InstrumentId } from './songTypes';

export type SampledInstrument = Exclude<InstrumentId, 'pad'>;

export function instKey(id: SampledInstrument, ref: number): string {
  return `inst.${id}.${ref}`;
}

export function drumKey(id: DrumId): string {
  return `drum.${id}`;
}

const REFS = new Map<SampledInstrument, number[]>(
  (Object.keys(INSTRUMENTS) as SampledInstrument[]).map((id) => [id, referenceNotes(INSTRUMENTS[id])]),
);

export function instrumentRefs(id: SampledInstrument): readonly number[] {
  return REFS.get(id) ?? [];
}

export class Sampler {
  /** Notes started (diagnostics and the CPU check). */
  notes = 0;

  constructor(private readonly ctx: BaseAudioContext, private readonly store: SoundStore) {}

  ready(id: SampledInstrument): boolean {
    return instrumentRefs(id).every((r) => this.store.has(instKey(id, r)));
  }

  /**
   * Plays `midi` on instrument `id` at context time `when` for `dur` seconds. Held instruments
   * release at note-off; struck ones are damped there. Returns false if not rendered yet.
   */
  note(id: SampledInstrument, midi: number, when: number, dur: number, vel: number, dest: AudioNode, detuneCents = 0, gainDb = 0): boolean {
    const def = INSTRUMENTS[id];
    const ref = nearestReference(instrumentRefs(id), midi);
    const bufs = this.store.get(instKey(id, ref));
    if (!bufs) return false;
    const ctx = this.ctx;
    const t0 = Math.max(when, ctx.currentTime);
    const src = ctx.createBufferSource();
    src.buffer = bufs[0];
    const rate = 2 ** ((midi - ref) / 12 + detuneCents / 1200);
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    const level = Math.max(0, vel) ** 1.6 * dbToGain(def.db + gainDb) * bufferGain(bufs[0]);
    g.gain.value = level;
    const off = t0 + Math.max(0.02, dur);
    g.gain.setValueAtTime(level, off);
    g.gain.setTargetAtTime(0, off, Math.max(0.005, def.release / 3));
    src.connect(g).connect(dest);
    src.start(t0);
    const natural = t0 + bufs[0].duration / rate;
    src.stop(Math.min(natural, off + def.release * 2.5) + 0.01);
    src.onended = () => {
      src.disconnect();
      g.disconnect();
    };
    this.notes++;
    return true;
  }

  drum(id: DrumId, when: number, vel: number, dest: AudioNode, gainDb = 0, rate = 1): boolean {
    const bufs = this.store.get(drumKey(id));
    if (!bufs) return false;
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    const buf = bufs[Math.floor(Math.random() * bufs.length) % bufs.length];
    src.buffer = buf;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = Math.max(0, vel) ** 1.4 * dbToGain(DRUMS[id].db + gainDb) * bufferGain(buf);
    src.connect(g).connect(dest);
    src.start(Math.max(when, ctx.currentTime));
    src.onended = () => {
      src.disconnect();
      g.disconnect();
    };
    this.notes++;
    return true;
  }
}

interface PadVoice { a: OscillatorNode; b: OscillatorNode; g: GainNode; midi: number }

/** §13.4 live pads: detuned-saw voices through one low-pass; chords glide between voicings. */
export class Pad {
  static readonly MAX_VOICES = 6;
  private readonly voices: PadVoice[] = [];
  private readonly filter: BiquadFilterNode;
  private readonly out: GainNode;
  private on = false;

  constructor(private readonly ctx: BaseAudioContext, dest: AudioNode, gainDb: number, cutoff = 1500) {
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = cutoff;
    this.filter.Q.value = 0.6;
    this.out = ctx.createGain();
    this.out.gain.value = dbToGain(gainDb);
    this.filter.connect(this.out).connect(dest);
  }

  get playing(): boolean {
    return this.on;
  }

  get voiceCount(): number {
    return this.voices.length;
  }

  /** Sounds `midis` (at most 6) from `when`, gliding held voices to the nearest new notes. */
  set(midis: readonly number[], when: number, detuneCents = 0, attack = 0.5): void {
    const notes = midis.slice(0, Pad.MAX_VOICES);
    const t = Math.max(when, this.ctx.currentTime);
    this.on = true;
    while (this.voices.length > notes.length) this.releaseVoice(this.voices.pop() as PadVoice, t, 0.6);
    for (let i = 0; i < notes.length; i++) {
      const f = 440 * 2 ** ((notes[i] - 69) / 12 + detuneCents / 1200);
      let v = this.voices[i];
      if (!v) {
        v = this.makeVoice(f, t, attack);
        this.voices.push(v);
      } else {
        v.a.frequency.setTargetAtTime(f, t, 0.04);
        v.b.frequency.setTargetAtTime(f, t, 0.04);
      }
      v.midi = notes[i];
    }
  }

  release(when: number, sec = 0.8): void {
    const t = Math.max(when, this.ctx.currentTime);
    for (const v of this.voices.splice(0)) this.releaseVoice(v, t, sec);
    this.on = false;
  }

  private makeVoice(f: number, t: number, attack: number): PadVoice {
    const ctx = this.ctx;
    const a = ctx.createOscillator();
    const b = ctx.createOscillator();
    a.type = 'sawtooth';
    b.type = 'sawtooth';
    a.frequency.value = f;
    b.frequency.value = f;
    a.detune.value = -8;
    b.detune.value = 8;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.16, t + attack);
    a.connect(g);
    b.connect(g);
    g.connect(this.filter);
    a.start(t);
    b.start(t);
    return { a, b, g, midi: 0 };
  }

  private releaseVoice(v: PadVoice, t: number, sec: number): void {
    v.g.gain.cancelScheduledValues(t);
    v.g.gain.setTargetAtTime(0, t, sec / 4);
    v.a.stop(t + sec * 1.5);
    v.b.stop(t + sec * 1.5);
    v.a.onended = () => {
      v.a.disconnect();
      v.b.disconnect();
      v.g.disconnect();
    };
  }

  dispose(when: number): void {
    this.release(when, 0.3);
    setTimeout(() => this.out.disconnect(), 1000);
  }
}
